import React from 'react';
import { useTranslation } from 'react-i18next';
import { useFadeInOnScroll } from '../../../hooks/useFadeInOnScroll';
import { Container } from '../../styles/sharedComponents';
import {
  FooterSection,
  FooterContent,
  FooterBrand,
  FooterColumn,
  FooterBottom,
  FooterCopyright,
  FooterSocials,
  SocialLink
} from './styles';

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const [footerRef, footerVisible] = useFadeInOnScroll<HTMLElement>({
    threshold: 0.1,
    rootMargin: '0px 0px -10% 0px',
    triggerOnce: true,
    reverseOnExit: false,
    delay: 0,
    direction: 'up',
    distance: 30,
  });

  return (
    <FooterSection ref={footerRef} $isVisible={footerVisible}>
      <Container>
        <FooterContent>
          <FooterBrand>
            <h3>{t('footer.brand.title')}</h3>
            <p>
              {t('footer.brand.description')}
            </p>
          </FooterBrand>

          <FooterColumn>
            <h4>{t('footer.product.title')}</h4>
            <ul>
              <li><a href="#features">{t('footer.product.links.features')}</a></li>
              <li><a href="#integrations">{t('footer.product.links.integrations')}</a></li>
              <li><a href="#pricing">{t('footer.product.links.pricing')}</a></li>
              <li><a href="#security">{t('footer.product.links.security')}</a></li>
            </ul>
          </FooterColumn>

          <FooterColumn>
            <h4>{t('footer.support.title')}</h4>
            <ul>
              <li><a href="#faq">{t('footer.support.links.faq')}</a></li>
              <li><a href="https://wa.me/555193332168" target="_blank" rel="noopener noreferrer">{t('footer.support.links.whatsapp')}</a></li>
              <li><a href="mailto:contato@loopra.com">{t('footer.support.links.email')}</a></li>
              <li><a href="#enterprise">{t('footer.support.links.enterprise')}</a></li>
            </ul>
          </FooterColumn>

          <FooterColumn>
            <h4>{t('footer.company.title')}</h4>
            <ul>
              <li><a href="#technology">{t('footer.company.links.technology')}</a></li>
              <li><a href="https://www.instagram.com/eddch_scl/" target="_blank" rel="noopener noreferrer">{t('footer.company.links.instagram')}</a></li>
              <li><a href="https://www.linkedin.com/in/eduardo-chamorra-5445a7380/" target="_blank" rel="noopener noreferrer">{t('footer.company.links.linkedin')}</a></li>
              <li><a href="https://wa.me/555193332168" target="_blank" rel="noopener noreferrer">{t('footer.company.links.contact')}</a></li>
            </ul>
          </FooterColumn>
        </FooterContent>

        <FooterBottom>
          <FooterCopyright>
            {t('footer.copyright')}
          </FooterCopyright>

          <FooterSocials>
            <SocialLink href="https://www.instagram.com/eddch_scl/" target="_blank" rel="noopener noreferrer">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <rect x="2" y="2" width="20" height="20" rx="5" strokeWidth="2" />
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" strokeWidth="2" />
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" strokeWidth="2" />
              </svg>
            </SocialLink>

            <SocialLink href="https://www.linkedin.com/in/eduardo-chamorra-5445a7380/" target="_blank" rel="noopener noreferrer">
              <svg fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
            </SocialLink>

            <SocialLink href="https://wa.me/555193332168" target="_blank" rel="noopener noreferrer">
              <svg fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.935L0 24l6.335-1.652a11.87 11.87 0 005.71 1.456h.006c6.555 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </SocialLink>
          </FooterSocials>
        </FooterBottom>
      </Container>
    </FooterSection>
  );
};

export default Footer;
